// ======================================================================
// File: src/modules/ppe/services/ppeOrderService.js
// Purpose:
//   PPE Phase-2 Orders + Engineering BOQ API client
//   (Aligned EXACTLY with backend routes)
// ======================================================================

import axiosClient from "../../../api/axiosClient";

/**
 * ----------------------------------------------------------------------
 * SALES ORDERS
 * ----------------------------------------------------------------------
 */

async function listOrders(params = {}) {
  const res = await axiosClient.get(
    "/api/ppe/orders/sales-orders",
    { params }
  );

  const payload = res.data?.data || {};

  const items = (payload.items || []).map(o => ({
    ...o,
    order_no: o.order_number,
    order_number: o.order_number,
  }));

  return {
    items,
    total: payload.total || 0,
    page: payload.page || 1,
    page_size: payload.page_size || params.page_size || 10,
  };
}

/**
 * Create NEW Sales / Production Order (DRAFT)
 * Backend: POST /api/ppe/orders/sales-orders
 */
async function createOrder(payload) {
  const res = await axiosClient.post(
    "/api/ppe/orders/sales-orders",
    payload
  );
  return res.data.data;
}

/**
 * List Order Items (Primary Planning Unit)
 * Backend: GET /api/ppe/orders/sales-orders/:orderId/items
 */
async function listOrderItems(orderId) {
  const res = await axiosClient.get(
    `/api/ppe/orders/sales-orders/${orderId}/items`
  );
  return res.data.data;
}

/**
 * ✅ CREATE ORDER ITEM (ADDED)
 * Backend: POST /api/ppe/orders/sales-orders/:orderId/items
 */
async function createOrderItem(orderId, payload) {
  const res = await axiosClient.post(
    `/api/ppe/orders/sales-orders/${orderId}/items`,
    payload
  );
  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * ENGINEERING BOQ (PER ORDER ITEM)
 * ----------------------------------------------------------------------
 */

async function getActiveBOQ(itemId) {
  const res = await axiosClient.get(
    `/api/ppe/orders/order-items/${itemId}/boq`
  );
  return res.data.data;
}

async function createBOQ(itemId) {
  const res = await axiosClient.post(
    `/api/ppe/orders/order-items/${itemId}/boq`
  );
  return res.data.data;
}

async function submitBOQ(boqId) {
  const res = await axiosClient.post(
    `/api/ppe/orders/boq/${boqId}/submit`
  );
  return res.data;
}

async function approveBOQ(boqId) {
  const res = await axiosClient.post(
    `/api/ppe/orders/boq/${boqId}/approve`
  );
  return res.data;
}

async function rejectBOQ(boqId, reason) {
  const res = await axiosClient.post(
    `/api/ppe/orders/boq/${boqId}/reject`,
    { reason }
  );
  return res.data;
}

async function replanBOQ(boqId) {
  const res = await axiosClient.post(
    `/api/ppe/orders/boq/${boqId}/replan`
  );
  return res.data;
}

/**
 * ----------------------------------------------------------------------
 * BOQ LINES (CRUD)
 * ----------------------------------------------------------------------
 */

async function listBOQLines(boqId) {
  const res = await axiosClient.get(
    `/api/ppe/orders/boq/${boqId}/lines`
  );
  return res.data.data;
}

async function createBOQLine(boqId, payload) {
  const res = await axiosClient.post(
    `/api/ppe/orders/boq/${boqId}/lines`,
    payload
  );
  return res.data.data;
}

async function updateBOQLine(lineId, payload) {
  const res = await axiosClient.patch(
    `/api/ppe/orders/boq/lines/${lineId}`,
    payload
  );
  return res.data.data;
}

async function deleteBOQLine(lineId) {
  const res = await axiosClient.delete(
    `/api/ppe/orders/boq/lines/${lineId}`
  );
  return res.data;
}

/**
 * ----------------------------------------------------------------------
 * ROUTING (READ-ONLY – EXECUTION SNAPSHOT)
 * ----------------------------------------------------------------------
 */

async function getRoutingAssignment(itemId) {
  const res = await axiosClient.get(
    `/api/ppe/routing/order-items/${itemId}/routing`
  );
  return res.data.data;
}

async function getRoutingOperations(itemId) {
  const res = await axiosClient.get(
    `/api/ppe/routing/order-items/${itemId}/routing/operations`
  );
  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * ROUTING (CONTROLLED OVERRIDE)
 * ----------------------------------------------------------------------
 */

async function listApprovedRoutings(productFamilyId) {
  const res = await axiosClient.get(
    "/api/ppe/routing/routings",
    { params: { product_family_id: productFamilyId } }
  );
  return res.data.data;
}

async function overrideRouting(itemId, payload) {
  const res = await axiosClient.post(
    `/api/ppe/routing/order-items/${itemId}/routing/override`,
    payload
  );
  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * EXPORT
 * ----------------------------------------------------------------------
 */
const ppeOrderService = {
  // Orders
  listOrders,
  createOrder,
  listOrderItems,
  createOrderItem, // ✅ EXPORTED

  // BOQ
  getActiveBOQ,
  createBOQ,
  submitBOQ,
  approveBOQ,
  rejectBOQ,
  replanBOQ,

  // BOQ Lines
  listBOQLines,
  createBOQLine,
  updateBOQLine,
  deleteBOQLine,

  // Routing (READ-ONLY)
  getRoutingAssignment,
  getRoutingOperations,

  // Routing (Override – controlled)
  listApprovedRoutings,
  overrideRouting,
};

export default ppeOrderService;
